import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import useAuth from "common/hooks/useAuth";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import {
  isMailCorrect,
  isPasswordCorrect,
} from "common/components/Password/Password";
import useRouter from "common/hooks/use-router";
import EditHeader from "common/components/EditHeader/EditHeader";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import RadioBlockItem from "common/components/RadioBlockItem/RadioBlockItem";
import rolesData from "conf/roleTypesData";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import fixFinalFormEmptyValues from "utils/fixFinalFormEmptyValues";
import dayjs from "dayjs";
import Icon from "components/Icon/Icon";
import ProfileCard from "components/ProfileCard/ProfileCard";
import { Profile } from "types/Profile";
import { User } from "types";

function UserForm({ onSubmit, editedUser, myAccount }: any) {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { navigate } = useRouter();
  const [changePassword, setChangePassword] = useState(false);

  const initialValues: User = useMemo(
    () =>
      editedUser?.id
        ? {
            ...editedUser,
            role: rolesData.find((r) => r.value === editedUser?.user?.role?.name)?.id || 1,
          }
        : {
            username: undefined,
            password: undefined,
            role: 1,
            email: '',
            phone: '',
            address: '',
            city: '',
            description: '',
            blocked: false,
            confirmed: true,
            isProvider: false,
            isCompany: false,
          },
    [editedUser]
  );

  const checkPassword = (value: string) =>
    isPasswordCorrect(value) ? undefined : t('common.passwordTooWeak');

  const checkEmail = (value: string) => (isMailCorrect(value) ? undefined : t('forms.badEmail'));

  const validatePasswords = (values: any) => {
    if (values.password !== values.confirmPassword) {
      return { confirmPassword: t('common.resetPasswordError') };
    }
    return {};
  };

  const onSubmitWrapper = async (values: User) => {
    if (!values.username) {
      // eslint-disable-next-line no-param-reassign
      values.username = values.email;
    }
    const res = await onSubmit(
      fixFinalFormEmptyValues(initialValues, {
        ...values,
        role: rolesData.find((r) => r.id === values.role.id)?.value,
      })
    );

    return res;
  };

  const displayRoleChoice = !myAccount && currentUser?.isAdmin && !(editedUser?.id > 0);

  return (
    <Form
      onSubmit={onSubmitWrapper}
      mutators={{
        ...arrayMutators,
      }}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      validate={(values: any) => (!editedUser ? validatePasswords(values) : undefined)}
      render={({ handleSubmit, values }) => (
        <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
          {/* Header */}
          <EditHeader
            title={
              myAccount
                ? `${t('titlePages.myAccount')}`
                : t(`common.${editedUser && editedUser.id ? 'edit' : 'add'}`)
            }
            subTitle={
              editedUser?.id
                ? `${values.username} - ${t('forms.subscribedThe', {
                    since: dayjs(values?.createdAt).format('DD/MM/YYYY'),
                  })}`
                : t('newItem.userLegend')
            }
            icon="UsersIcon"
            HeaderButtonsEdit={
              <HeaderButtonsEdit
                inHeader
                onSubmit={handleSubmit}
                onCancel={myAccount ? null : () => navigate('/users')}
              />
            }
          />
          <form onSubmit={handleSubmit} noValidate autoComplete="off">
            <FormScrollError />

            <div className="responsive-flex gap-2 mt-2">
              <div className={`${editedUser?.id ? 'md:w-2/3' : 'w-full'} flex flex-col gap-2`}>
                <div className="white-box">
                  {displayRoleChoice && (
                    <>
                      <SectionTitle icon="CalendarDaysIcon" title={t('forms.role')} />
                      <FormControl
                        type="custom"
                        name="role"
                        inputContainerStyle="flex gap-3 mb-4"
                        containerStyle="w-full"
                      >
                        <RadioBlock
                          Component={RadioBlockItem}
                          items={rolesData.filter(
                            (role) =>
                              role.value !== 'SuperAdmin' ||
                              currentUser?.role?.name === 'SuperAdmin'
                          )}
                          horizontal
                        />
                      </FormControl>
                    </>
                  )}
                  <div className="responsive-flex gap-4">
                    <div className="flex flex-col w-full gap-2">
                      <SectionTitle title={t('forms.loginInfos')} icon="CalendarDaysIcon" />

                      <FormControl
                        name="email"
                        titleKey="forms.email"
                        required
                        validate={checkEmail}
                        // containerStyle="mb-2"
                      />

                      {!!editedUser?.id && (
                        <div className="my-2">
                          <ToggleSwitch
                            rightLabel={t('forms.newPassword')}
                            value={changePassword}
                            onChange={setChangePassword}
                          />
                        </div>
                      )}
                      {(changePassword || !editedUser?.id) && (
                        <>
                          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                            <FormControl
                              type="jpassword"
                              name="password"
                              titleKey="forms.password"
                              required
                              validate={checkPassword}
                            />
                            <FormControl
                              name="confirmPassword"
                              title={t('forms.confirmPassword')}
                              type="password"
                            />
                          </div>
                          {t('common.passwordInstruction')}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {editedUser?.id && (
                  <div className="white-box">
                    <div className="responsive-flex gap-4">
                      <div className="flex flex-col w-full gap-2">
                        <SectionTitle title={t('forms.profilesInfos')} icon="Profile" />
                        {values.profiles?.map((profile: Profile) => (
                          <ProfileCard
                            profile={profile}
                            noTitle
                            onClick={() => {
                              if (profile?.id) {
                                navigate(`/profiles/edit/${profile.id}`);
                              }
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {editedUser?.id && (
                <div className="flex flex-col md:w-1/3 gap-2">
                  <div className="white-box">
                    <div className="flex flex-col w-full gap-2">
                      <SectionTitle title={t('forms.accountStatuses')} icon="Status" />

                      <div className="text-sm w-full px-1 pt-0 pb-4">
                        {t('forms.subscribedThe', {
                          since: dayjs(values?.createdAt).format('DD/MM/YYYY'),
                        })}
                      </div>

                      {/* TODO : Keep only the status, not the FormControl for the confirmed property.
                      For testing until email is setup */}
                      {values.confirmed ? (
                        <div className="flex flex-rox items-center w-full gap-2">
                          {values.confirmed ? (
                            <Icon name="CheckCircleIcon" className="w-8 h-8 text-green-500" />
                          ) : (
                            <Icon name="XCircleIcon" className="w-8 h-8 text-red-500" />
                          )}
                          <div className="text-md">{t('common.verifiedEmail')}</div>
                        </div>
                      ) : (
                        <FormControl type="custom" name="confirmed" containerStyle="my-2">
                          <ToggleSwitch
                            rightLabel={t('forms.confirmed')}
                            isDisabled={currentUser?.id === values.id}
                          />
                        </FormControl>
                      )}

                      <FormControl type="custom" name="blocked" containerStyle="my-2">
                        <ToggleSwitch
                          rightLabel={t('forms.blocked')}
                          isDisabled={currentUser?.id === values.id}
                        />
                      </FormControl>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <HeaderButtonsEdit
              onSubmit={handleSubmit}
              onCancel={myAccount ? null : () => navigate('/users')}
            />
          </form>
        </div>
      )}
    />
  );
}
export default UserForm;
